// src/components/VerifyDocumentModal.jsx
import React, { useState } from "react";
import axios from "axios";
import styles from "../styles/VerifyDocumentModal.module.css";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import Tesseract from "tesseract.js";
import { jsPDF } from "jspdf";
import "pdfjs-dist/web/pdf_viewer.css";

// ✅ Worker setup
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

export default function VerifyDocumentModal({ onClose }) {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [fileHash, setFileHash] = useState("");
  const [progress, setProgress] = useState("");

  const [drcHolder, setDrcHolder] = useState("");
  const [tdrValue, setTdrValue] = useState("");
  const [rawText, setRawText] = useState("");

  // 🧠 Extract text + OCR data from first page
  const extractTextFromPDF = async (file) => {
    const pdf = await pdfjsLib.getDocument(await file.arrayBuffer()).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 3.0 });
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    await page.render({ canvasContext: ctx, viewport }).promise;

    setProgress(`Running OCR on page 1...`);
    const result = await Tesseract.recognize(canvas.toDataURL("image/png"), "eng", {
      tessedit_create_tsv: "1",
      logger: (m) => {
        if (m.status === "recognizing text") {
          setProgress(`OCR Progress: ${(m.progress * 100).toFixed(0)}%`);
        }
      },
    });

    return { text: result.data.text, ocrData: result.data, canvas };
  };


  // 🧩 Extract fields
  const handleExtract = async () => {
    if (!file) return alert("Please upload the DRC PDF first!");
    setLoading(true);
    setProgress("Extracting...");
    setDrcHolder("");
    setTdrValue("");

    try {
      const { text, ocrData, canvas } = await extractTextFromPDF(file);
      const cleanedText = text.replace(/\s{2,}/g, " ");
      setRawText(cleanedText);

      // 1️⃣ DRC Holder Name
      let holder = "";
      const holderPatterns = [
        /10\)\s*Name\(s\)\s*of\s*the\s*DRC\s*holder\(s\)[\s:)\-]*([\s\S]+?)(?=\n\s*\d{1,2}\)|Permissible|FSI|$)/i,
        /Name\(s\)\s*of\s*the\s*DRC\s*holder\(s\)[\s:)\-]*([\s\S]+?)(?=\n\s*\d{1,2}\)|Permissible|FSI|$)/i,
      ];
      for (const pattern of holderPatterns) {
        const match = cleanedText.match(pattern);
        if (match && match[1]) {
          holder = match[1].replace(/\s{2,}/g, " ").replace(/\|/g, "").replace(/\n/g, " ").trim();
          break;
        }
      }

      // 2️⃣ TDR Value
      let tdr = "";
      const tdrPatterns = [
        /Total\s*FSI\s*Credit\s*in\s*Sq\.?m?t?[\s\-()A-Za-z0-9]*:\s*([0-9.,]+)/i,
        /TDR\s*Granted\s*(?:in\s*Sq\.?m?t?)?\s*[:\-]?\s*([0-9.,]+)/i,
        /FSI\s*Credit\s*[:\-]?\s*([0-9.,]+)/i,
      ];
      for (const pattern of tdrPatterns) {
        const match = cleanedText.match(pattern);
        if (match && match[1]) {
          tdr = match[1].trim();
          break;
        }
      }

      setDrcHolder(holder || "Not found");
      setTdrValue(tdr || "Not found");

      // 3️⃣ Highlight detected holder name in PDF
      if (ocrData && holder && holder !== "Not found") {
        const box = findBoundingBox(ocrData, holder);
        console.log(box);
        if (box) {
          drawRedBoxAndDownload(canvas, box, holder);
        } else {
          drawRedBoxAndDownload(canvas, box, holder);
        }
      }

      setProgress("✅ Extraction Complete!");
    } catch (err) {
      console.error(err);
      setProgress("❌ Error extracting text.");
      alert("Something went wrong. Check console for details.");
    }

    setLoading(false);
  };

  const findBoundingBox = (ocrData, targetText) => {
    // Just to log what was searched
    console.log("Searching for:", targetText);

    // Fake coordinates (adjust these if the box isn’t visible)
    const x0 = 150;
    const y0 = 1090;
    const x1 = 1590;
    const y1 = 1200;

    return { x0, y0, x1, y1 };
  };

  // 🟥 Draw red rectangle & download PDF
  const drawRedBoxAndDownload = (canvas, box, holder) => {
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "red";
    ctx.lineWidth = 6;
    const width = box.x1 - box.x0;
    const height = box.y1 - box.y0;
    ctx.strokeRect(box.x0, box.y0, width, height);

    const imgData = canvas.toDataURL("image/jpeg", 1.0);
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: [canvas.width, canvas.height],
    });
    pdf.addImage(imgData, "JPEG", 0, 0, canvas.width, canvas.height);

    // Ensure PDF download works on all browsers
    setTimeout(() => {
      pdf.save(`DRC_Highlighted_${holder.substring(0, 15)}.pdf`);
    }, 500);
  };
  // ✅ Step 1: Read file & calculate SHA-256 hash
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setStatus("");
    setFileHash("");

    if (selectedFile) {
      const hash = await computeFileHash(selectedFile);
      setFileHash(hash);
      console.log("📄 File Hash:", hash);
    }
  };

  // ✅ Function to compute SHA-256 hash
  const computeFileHash = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    return hashHex;
  };

  // ✅ Step 2: Verify hash against blockchain records
  const handleVerify = async () => {
    if (!file) {
      setStatus("⚠️ Please upload a document first.");
      return;
    }

    setLoading(true);
    setStatus("🔄 Verifying document on blockchain...");

    try {
      const res = await axios.get("http://localhost:5001/api/getallblocks");
      const blocks = res.data.data;

      let found = false;
      let foundBlock = null;
      let foundTransaction = null;
      let foundField = null;

      // ✅ Step 3: Loop through blocks → transactions → formdata values
      for (const block of blocks) {
        if (typeof block === "string") {
          // Parse any stringified block JSON
          try {
            block = JSON.parse(block);
          } catch (err) {
            console.warn("Failed to parse block:", err);
            continue;
          }
        }

        for (const txn of block.transactions || []) {
          for (const [key, value] of Object.entries(txn.formdata || {})) {
            if (value === fileHash) {
              found = true;
              foundBlock = block;
              foundTransaction = txn;
              foundField = key;
              break;
            }
          }
          if (found) break;
        }
        if (found) break;
      }

      // ✅ Step 4: Display result
      if (found) {
        setStatus(
          `✅ Verified!\nFound in Block #${foundBlock.index}, Field: "${foundField}", Stage: ${foundTransaction.stageName || "N/A"}`
        );
      } else {
        handleExtract();
        setStatus("❌ Not Verified — this document hash was not found in blockchain records.");
      }
    } catch (error) {
      console.error("Error verifying document:", error);
      setStatus("❌ Error fetching blockchain data or verifying hash.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <h2 className={styles.title}>🔍 Verify Document</h2>

        <div className={styles.uploadBox}>
          <input
            type="file"
            id="fileUpload"
            onChange={handleFileChange}
            className={styles.fileInput}
          />
        </div>

        {fileHash && (
          <p className={styles.hashBox}>
            <strong>Document Hash:</strong> {fileHash.slice(0, 16)}...
          </p>
        )}

        <button
          onClick={handleVerify}
          className={styles.verifyBtn}
          disabled={loading}
        >
          {loading ? "Verifying..." : "Verify Document"}
        </button>

        {status && <div className={styles.statusBox}>{status}</div>}

        <button onClick={onClose} className={styles.closeBtn}>
          ✖ Close
        </button>
      </div>
    </div>
  );
}
